// HtmlInputFormAttributes.jsx
import React from "react";

const HtmlInputFormAttributes = () => {
  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif", lineHeight: "1.6" }}>
      <h1>HTML Input Form Attributes - Comprehensive Guide</h1>

      {/* Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          Input form attributes define how an input element behaves inside a form. They control submission, validation, accessibility, and interactivity.
        </p>
      </section>

      {/* Form Association */}
      <section id="form-association">
        <h2>2. Form Association Attributes</h2>
        <ul>
          <li>
            <strong>form:</strong> Associates an input with a specific form by ID.
          </li>
          <li>
            <strong>name:</strong> Identifies input data when the form is submitted.
          </li>
        </ul>
        <pre>
          <code>
{`<form id="myForm" action="/submit" method="post">
  <input type="text" name="username" form="myForm" placeholder="Username" />
  <button type="submit" form="myForm">Submit</button>
</form>`}
          </code>
        </pre>
      </section>

      {/* Validation Attributes */}
      <section id="validation-attributes">
        <h2>3. Validation Attributes</h2>
        <p>Attributes that enforce rules on form inputs:</p>
        <ul>
          <li><strong>required:</strong> Input must be filled before submitting.</li>
          <li><strong>pattern:</strong> Custom regex validation.</li>
          <li><strong>min / max:</strong> Numeric or date limits.</li>
          <li><strong>minlength / maxlength:</strong> Character length limits.</li>
          <li><strong>step:</strong> Increment value for numeric/date inputs.</li>
        </ul>
        <pre>
          <code>
{`<input type="text" name="username" minlength="4" maxlength="12" required />
<input type="number" name="age" min="18" max="65" step="1" required />`}
          </code>
        </pre>
      </section>

      {/* Accessibility Attributes */}
      <section id="accessibility">
        <h2>4. Accessibility Attributes</h2>
        <ul>
          <li><strong>aria-label:</strong> Provides a descriptive label for screen readers.</li>
          <li><strong>aria-describedby:</strong> Associates the input with explanatory text.</li>
          <li><strong>title:</strong> Tooltip displayed on hover.</li>
        </ul>
        <pre>
          <code>
{`<label htmlFor="email">Email:</label>
<input type="email" id="email" name="email" aria-label="User email" title="Enter your email address" />
<p id="email-desc">We'll never share your email.</p>
<input type="email" aria-describedby="email-desc" />`}
          </code>
        </pre>
      </section>

      {/* Autofocus & Autocomplete */}
      <section id="autofocus-autocomplete">
        <h2>5. Autofocus & Autocomplete</h2>
        <ul>
          <li><strong>autofocus:</strong> Automatically focuses the input on page load.</li>
          <li><strong>autocomplete:</strong> Enables or disables browser autocomplete.</li>
        </ul>
        <pre>
          <code>
{`<input type="text" name="firstname" autofocus placeholder="First Name" />
<input type="email" name="email" autocomplete="on" placeholder="Email Address" />`}
          </code>
        </pre>
      </section>

      {/* Input Type Specific Attributes */}
      <section id="type-specific">
        <h2>6. Input Type Specific Attributes</h2>
        <ul>
          <li><strong>multiple:</strong> Allows multiple file selection for file inputs.</li>
          <li><strong>accept:</strong> Restricts file types.</li>
          <li><strong>readonly:</strong> Prevents editing but allows selection.</li>
          <li><strong>disabled:</strong> Disables the input completely.</li>
        </ul>
        <pre>
          <code>
{`<input type="file" name="resume" multiple accept=".pdf,.docx" />
<input type="text" name="username" readonly value="JohnDoe" />
<input type="text" name="lastname" disabled placeholder="Disabled input" />`}
          </code>
        </pre>
      </section>

      {/* Event Attributes */}
      <section id="event-attributes">
        <h2>7. Event Attributes</h2>
        <p>React/HTML events for input interactions:</p>
        <ul>
          <li>onChange: Triggered when input value changes.</li>
          <li>onFocus: Triggered when input receives focus.</li>
          <li>onBlur: Triggered when input loses focus.</li>
          <li>onInput: Triggered as the user types.</li>
        </ul>
        <pre>
          <code>
{`<input 
  type="text" 
  onChange={(e) => console.log("Changed:", e.target.value)} 
  onFocus={() => console.log("Focused")} 
  onBlur={() => console.log("Blurred")} 
/>`}
          </code>
        </pre>
      </section>

      {/* Best Practices */}
      <section id="best-practices">
        <h2>8. Best Practices</h2>
        <ul>
          <li>Always associate inputs with <strong>labels</strong> using htmlFor and id.</li>
          <li>Use validation attributes to prevent errors.</li>
          <li>Keep inputs accessible with proper ARIA attributes.</li>
          <li>Combine multiple attributes thoughtfully (e.g., required + pattern + title).</li>
          <li>Use readonly or disabled to control user input appropriately.</li>
        </ul>
      </section>
    </div>
  );
};

export default HtmlInputFormAttributes;
